import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  message,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useConnection } from '@sezenta/connection';

type Option = { label: string; value: string };
type FoundUser = {
  userId: string;
  name: string;
  email: string;
  picture?: string | null;
};

// CR004: email-first "attach or create" for the in-project Users page.
//  1. Admin enters an email.
//  2. We look it up (exact match, no directory browsing):
//     - already a member  -> blocked with a message
//     - exists, not member -> attach form (this project's roles/driver/state only;
//                             the shared identity is read-only and not edited here)
//     - not found          -> hand back to the parent to open the full create form
//                             prefilled with the email.
const AddUserModal: React.FC<{
  open: boolean;
  onClose: () => void;
  businessId: string;
  roleOptions: Option[];
  stateOptions: Option[];
  onAttached: () => void;
  onCreateNew: (email: string) => void;
}> = ({
  open,
  onClose,
  businessId,
  roleOptions,
  stateOptions,
  onAttached,
  onCreateNew,
}) => {
  const connection = useConnection();
  const [step, setStep] = useState<'email' | 'attach'>('email');
  const [looking, setLooking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [found, setFound] = useState<FoundUser | null>(null);
  const [emailForm] = Form.useForm();
  const [attachForm] = Form.useForm();

  const reset = () => {
    setStep('email');
    setFound(null);
    setLooking(false);
    setSubmitting(false);
    emailForm.resetFields();
    attachForm.resetFields();
  };

  const close = () => {
    reset();
    onClose();
  };

  const onContinue = async () => {
    let email;
    try {
      ({ email } = await emailForm.validateFields());
    } catch {
      return; // inline validation errors are shown by antd
    }
    setLooking(true);
    try {
      const res = await connection.get('users/lookup', {
        params: { email: String(email).trim(), businessId },
      });
      const data = res.data as {
        found: boolean;
        isMember: boolean;
        user?: FoundUser;
      };
      if (data.isMember) {
        message.warning('This user is already a member of this project.');
        return;
      }
      if (data.found && data.user) {
        setFound(data.user);
        attachForm.setFieldsValue({ roleIds: [], isDriver: false, state: [] });
        setStep('attach');
        return;
      }
      // Brand new email → defer to the full create form, prefilled.
      onCreateNew(String(email).trim());
      close();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ?? 'Could not look up that email.',
      );
    } finally {
      setLooking(false);
    }
  };

  const onAttach = async () => {
    if (!found) return;
    let values;
    try {
      values = await attachForm.validateFields();
    } catch {
      return; // inline validation errors are shown by antd
    }
    setSubmitting(true);
    try {
      await connection.post(`users/${businessId}/attach`, {
        userId: found.userId,
        roleIds: values.roleIds,
        isDriver: values.isDriver ?? false,
        state: values.state,
      });
      message.success(`${found.name} was added to this project.`);
      onAttached();
      close();
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? 'Could not add this user.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={step === 'email' ? 'Add user' : 'Add existing user'}
      open={open}
      onCancel={close}
      destroyOnClose
      footer={
        step === 'email' ? (
          <div className="flex justify-end gap-2">
            <Button onClick={close}>Cancel</Button>
            <Button type="primary" loading={looking} onClick={onContinue}>
              Continue
            </Button>
          </div>
        ) : (
          <div className="flex justify-between gap-2">
            <Button onClick={() => setStep('email')}>Back</Button>
            <div className="flex gap-2">
              <Button onClick={close}>Cancel</Button>
              <Button type="primary" loading={submitting} onClick={onAttach}>
                Add to project
              </Button>
            </div>
          </div>
        )
      }
    >
      {step === 'email' ? (
        <Form form={emailForm} layout="vertical" onFinish={onContinue}>
          <div className="text-[13px] text-[#84919A] mb-3">
            Enter the user&apos;s email. If they already have an account
            we&apos;ll add them to this project; otherwise you can create a new
            one.
          </div>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is required.' },
              { type: 'email', message: 'Enter a valid email address.' },
            ]}
          >
            <Input placeholder="name@example.com" autoFocus size="large" />
          </Form.Item>
        </Form>
      ) : (
        <>
          {found && (
            <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-[#F7F9FB] border border-[#EAECF0]">
              <Avatar
                src={found.picture || undefined}
                icon={!found.picture ? <UserOutlined /> : undefined}
                size={44}
              />
              <div>
                <div className="text-[15px] font-semibold text-[#18191C]">
                  {found.name}
                </div>
                <div className="text-[13px] text-[#84919A]">{found.email}</div>
              </div>
              <Tag color="cyan" className="ml-auto">
                Existing user
              </Tag>
            </div>
          )}
          <div className="text-[12px] text-[#84919A] mb-3">
            This only sets their access in <strong>this project</strong>. Their
            name, login and avatar are shared and aren&apos;t changed here.
          </div>
          <Form form={attachForm} layout="vertical">
            <Form.Item
              name="roleIds"
              label="Roles"
              rules={[{ required: true, message: 'Select at least one role.' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select roles for this project"
                options={roleOptions}
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item name="state" label="States">
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Select permitted states (optional)"
                options={stateOptions}
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item name="isDriver" valuePropName="checked">
              <Checkbox>Is a driver</Checkbox>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default AddUserModal;
